import { User, UserRecord } from '@shared/models/user.model';
import { devtools } from 'zustand/middleware';
import axios from 'axios';
import { toError } from '@shared/helpers/errors.helper';
import { toLookup } from '@shared/helpers/utils.helpers';
import { getHmrStoreState, initHmrStore } from '../../helpers/vite.helpers';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { isEqual } from 'lodash';

const STORE_NAME = 'users';

/** Drops a late `/users` response when a newer organization fetch has started. */
let latestUsersFetchGeneration = 0;

export type UserState = {
  error: string;
  isLoading: boolean;
  user: User | undefined;
  users: User[];
  lookup: UserRecord;
  /** Current user. Rethrows the original network error so token refresh can retry. */
  fetchUser: (id: string) => Promise<User>;

  /** fetch all users in the organization */
  fetchUsers: (organization: string) => Promise<void>;
};

export const useUserStore = createWithEqualityFn<UserState>()(
  devtools(
    (set, get) => ({
      error: '',
      isLoading: false,
      user: undefined,
      users: [],
      lookup: {},
      // HMR reloads this module; keep the logged-in user so Auth0TokenRefresher
      // does not skip refetch because organizations are still in memory.
      ...getHmrStoreState(STORE_NAME),
      fetchUsers: (organization: string) => {
        const fetchGeneration = ++latestUsersFetchGeneration;
        set(() => ({
          error: '',
          isLoading: true,
          users: [],
          lookup: {},
        }));

        return axios
          .get<User[]>('/users', { headers: { Organization: organization } })
          .then(({ data }) => {
            // The first organization can be selected before the URL org is
            // applied. Its response must not replace the selected org's members.
            if (fetchGeneration !== latestUsersFetchGeneration) return;

            const { users: currentUsers } = get();
            if (isEqual(currentUsers, data)) {
              set(() => ({ isLoading: false, error: '' }));
              return;
            }

            set(() => ({
              isLoading: false,
              error: '',
              users: data,
              lookup: toLookup(data),
            }));
          })
          .catch(() => {
            if (fetchGeneration !== latestUsersFetchGeneration) return;
            set(() => ({ error: 'Could not fetch users', isLoading: false }));
          });
      },
      fetchUser: (id) => {
        return axios
          .get<User>(`/users/${id}`)
          .then(({ data }) => {
            set(() => ({ user: data }));
            return data;
          })
          .catch((err: unknown) => {
            // Rethrow the original axios/network error so token refresh can
            // retry while the API is down instead of wrapping it as a new Error.
            throw toError(err);
          });
      },
    }),
    { name: STORE_NAME },
  ),
  shallow,
);

initHmrStore(STORE_NAME, useUserStore);
